import { BOSSES } from './bosses.js';
import { EchoLance } from '../systems/spawning/patterns/echo/EchoLance.js';
import { EchoCone } from '../systems/spawning/patterns/echo/EchoCone.js';
import { EchoOrbit } from '../systems/spawning/patterns/echo/EchoOrbit.js';
import { RadialRing } from '../systems/spawning/patterns/RadialRing.js';
import { HomingShardVolley } from '../systems/spawning/patterns/HomingShardVolley.js';

/**
 * Echo Duel: builds a miniboss bossDef out of a recorded run snapshot
 * (SaveManager.lastEchoSnapshot, captured in Game._endRun). The ghost's
 * attack pool is one hostile mirror per recorded weapon — it literally
 * fights with the build you brought to your last run, scaled by each
 * weapon's recorded level. Spawned through the same secondary-boss slot the
 * herald pairs use (Game._updateEchoRift); `isEchoGhost` is what routes its
 * death away from the herald-queue logic in _onMinibossDied.
 */

// One mirror per weapon name. Harmonics fusions mirror as their flashier
// parent's pattern — the ghost doesn't need a 1:1 replica, just a kit that
// visibly rhymes with the recorded build.
const WEAPON_MIRRORS = {
  'Spirit Lance': (level) => new EchoLance({ config: { damage: 10, speed: 300, level } }),
  'Scatter Cannon': (level) => new EchoCone({ config: { damage: 8, speed: 190, level } }),
  'Arc Needle': (level) => new HomingShardVolley({ minPhase: 0, config: { damage: 8, speed: 75 + level * 3, turnRate: 1.6 } }),
  'Orbit Ward': (level) => new EchoOrbit({ config: { damage: 8, speed: 70, level } }),
  'Nova Pulse': (level) => new RadialRing({ minPhase: 0, config: { damage: 8, speed: 55 + level * 3 } }),
  'Storm Lance': (level) => new EchoLance({ config: { damage: 11, speed: 320, level } }),
  'Cannonade': (level) => new EchoCone({ config: { damage: 9, speed: 210, level } }),
  'Nova Ward': (level) => new EchoOrbit({ config: { damage: 9, speed: 75, level } }),
  'Static Bloom': (level) => new RadialRing({ minPhase: 0, config: { damage: 9, speed: 60 + level * 3 } }),
};

const MAX_MIRRORS = 4; // bound the kit so a six-weapon endless build doesn't produce an unreadable fight

export function buildEchoBossDef(snapshot, { hpMultiplier = 1 } = {}) {
  // Riven Choir's cosmetics — the "echo" flavor already belongs to it.
  // Looked up by spriteKey rather than position: the 2026-07 gauntlet
  // expansion moved the Choir to slot 2 (and may move it again).
  const base = BOSSES.find((b) => b.spriteKey === 'rivenChoir') ?? BOSSES[0];
  const weapons = (snapshot.weapons ?? [])
    .filter((w) => WEAPON_MIRRORS[w.name])
    .slice(0, MAX_MIRRORS);

  return {
    ...base,
    name: `Echo of ${snapshot.profileName || 'a Survivor'}`,
    maxHp: Math.round((2500 + 500 * Math.max(0, snapshot.gauntletIndex ?? 0)) * hpMultiplier),
    moveSpeed: 40,
    layerFromPhase: 99, // one mirror at a time — a duel, not a barrage
    pickCooldownMultiplier: 0.85,
    isMiniboss: true,
    isEchoGhost: true,
    // Factories, not instances — same reasoning as createMiniboss in
    // bosses.js: a fresh, un-mutated pattern set per encounter.
    createPatterns: () => (weapons.length > 0
      ? weapons.map((w) => WEAPON_MIRRORS[w.name](w.level ?? 1))
      : [new RadialRing({ minPhase: 0, config: { damage: 8, speed: 60 } })]),
    createPhase2Pattern: undefined,
    createDesperationPattern: undefined,
  };
}
